/**
 * Hook for tracking cumulative Claude API token usage.
 * Stores usage in localStorage and provides methods to track stats.
 */
import { useState, useEffect, useCallback } from 'react';
import type { TokenUsage } from '../types';

const STORAGE_KEY = 'vocabulizer_token_usage';
const CONTEXT_WINDOW = 200000; // Claude Sonnet 4.5 context window

export function useTokenUsage() {
  const [usage, setUsage] = useState<TokenUsage>({
    totalInputTokens: 0,
    totalOutputTokens: 0,
    totalTokens: 0,
    sessionInputTokens: 0,
    sessionOutputTokens: 0,
    sessionTokens: 0,
    requestCount: 0,
    lastReset: new Date().toISOString(),
  });

  // Load usage from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as TokenUsage;
        setUsage(parsed);
      }
    } catch (err) {
      console.error('Failed to load token usage from localStorage', err);
    }
  }, []);

  // Save usage to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(usage));
    } catch (err) {
      console.error('Failed to save token usage to localStorage', err);
    }
  }, [usage]);

  // Add new token usage from an API call
  const addUsage = useCallback((tokenUsage: { inputTokens?: number; outputTokens?: number; totalTokens?: number }): void => {
    if (!tokenUsage) return;

    const { inputTokens = 0, outputTokens = 0, totalTokens = 0 } = tokenUsage;

    setUsage((prev) => ({
      totalInputTokens: prev.totalInputTokens + inputTokens,
      totalOutputTokens: prev.totalOutputTokens + outputTokens,
      totalTokens: prev.totalTokens + totalTokens,
      sessionInputTokens: prev.sessionInputTokens + inputTokens,
      sessionOutputTokens: prev.sessionOutputTokens + outputTokens,
      sessionTokens: prev.sessionTokens + totalTokens,
      requestCount: prev.requestCount + 1,
      lastReset: prev.lastReset,
    }));
  }, []);

  // Reset session stats (keeps lifetime totals)
  const resetSession = useCallback((): void => {
    setUsage((prev) => ({
      ...prev,
      sessionInputTokens: 0,
      sessionOutputTokens: 0,
      sessionTokens: 0,
      lastReset: new Date().toISOString(),
    }));
  }, []);

  // Reset all stats
  const resetAll = useCallback((): void => {
    setUsage({
      totalInputTokens: 0,
      totalOutputTokens: 0,
      totalTokens: 0,
      sessionInputTokens: 0,
      sessionOutputTokens: 0,
      sessionTokens: 0,
      requestCount: 0,
      lastReset: new Date().toISOString(),
    });
  }, []);

  // Calculate percentage of context window used (based on session)
  const contextPercentage: number = Math.round((usage.sessionTokens / CONTEXT_WINDOW) * 100);

  return {
    usage,
    addUsage,
    resetSession,
    resetAll,
    contextPercentage,
    contextWindow: CONTEXT_WINDOW,
  };
}
